import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { i18n } from '@/lib/i18n'
import { aiModeRequestBody, type ConvAiChoice } from '@/lib/ai-khung'
import { api } from '@/lib/api-client'
import { createCoalescer } from '@/lib/coalesce'
import { flattenConversationPages, flattenMessagePages, nextPageParam } from '@/lib/paged-lists'
import { ERROR_REFETCH_MS, retryDelayMs, shouldRetryQuery } from '@/lib/query-retry'
import {
  getSocket,
  joinConversation,
  leaveConversation,
} from '@/lib/socket'

// ── Kiểu dữ liệu ─────────────────────────────────────────────────────
export type AiMode = 'manual' | 'auto' | 'suggest'
/**
 * Tab danh sách hội thoại (giống Zalo): 'main' = "Ưu tiên" (mặc định),
 * 'internal' = "Nội bộ" — trao đổi nội bộ, backend loại khỏi mọi báo cáo.
 */
export type ConvTab = 'main' | 'internal'
export type SenderType = 'self' | 'contact' | 'system'

export interface ChatContact {
  id: string
  fullName: string | null
  phone: string | null
  email: string | null
  avatarUrl: string | null
  /** uid Zalo cá nhân — API đã trả sẵn (`GET /conversations/:id` include cả Contact), dùng cho menu "Nhắn tin riêng"/"Thêm bạn". */
  zaloUid?: string | null
  leadScore?: number | null
  lifecycleStage?: string | null
  status?: string | null
  aiSummary?: string | null
  aiSentimentLabel?: string | null
  aiIntent?: string | null
  tags?: string[] | null
  // Các trường dưới đây API đã trả sẵn (GET /conversations/:id include cả bản ghi
  // Contact) nhưng trước giờ chưa khai báo nên panel không dùng được.
  jobTitle?: string | null
  birthday?: string | null
  source?: string | null
  firstContactDate?: string | null
  lastActivity?: string | null
  nextAppointment?: string | null
  assignedUserId?: string | null
  notes?: string | null
  /** Bài toán khách đang gặp — AI rút từ hội thoại. */
  aiPainPoints?: string[] | null
  /** Đối thủ khách đang cân nhắc. */
  aiCompetitors?: string[] | null
  /** Tín hiệu mua, ví dụ "Yêu cầu báo giá". */
  aiSignals?: string[] | null
  aiSentimentReason?: string | null
}

export interface ChannelAccount {
  id: string
  displayName: string
  externalUid?: string | null
  platform: number
}

export interface LastMessagePreview {
  id: string
  content: string | null
  contentType: string
  senderType: SenderType
  senderName: string | null
  sentAt: string
  isDeleted?: boolean
}

export interface ConversationListItem {
  id: string
  displayName: string
  unreadCount: number
  isReplied: boolean
  tab: ConvTab
  aiMode: AiMode
  lastMessageAt: string | null
  isPinned?: boolean
  contact: ChatContact | null
  channelAccount: ChannelAccount | null
  messages: LastMessagePreview[]
  /** AI phục vụ hội thoại lúc này (backend chat/ai-inbox-view.ts) — chỉ có ở danh sách. */
  aiAgent?: AiAgentInfo
}

/** auto = tự trả lời · suggest = chỉ gợi ý · manual = AI tắt / đã chuyển người · paused · offline · off = tắt toàn tổ chức. */
export type AiAgentState = 'auto' | 'suggest' | 'manual' | 'paused' | 'offline' | 'off'
export interface AiAgentInfo {
  /** null = AI chung của tổ chức (kênh không có nhân viên AI nào). */
  botId: string | null
  botName: string | null
  state: AiAgentState
}

export interface ConversationDetail extends ConversationListItem {
  /** Người phụ trách hội thoại (null = chưa ai nhận). */
  assignedUserId?: string | null
  assignee?: { id: string; fullName: string; avatarUrl?: string | null } | null
  /** Việc người xem được làm: Nhận (chưa ai nhận) / Giao lại (admin, quản lý kênh, hoặc đang phụ trách). Luật thật ở máy chủ. */
  assignAccess?: { claim: boolean; assign: boolean }
  aiModeReason?: string | null
  aiPausedUntil?: string | null
  externalThreadId?: string | null
  threadType?: string
  /** Ghi đè luật nhắc tên cho riêng hội thoại này; null = theo cài đặt chung. */
  requireMention?: boolean | null
  /** Ai đã xem tới đâu (từ sự kiện seen của Zalo): uid → mốc tin mới nhất đã xem. */
  seenBy?: Record<string, { msgId: string; msgSentAt: string; seenAt: string }> | null
}

export interface MessageAttachment {
  url: string
  type: string
  thumb?: string
  title?: string
}

/** Nguồn tin gửi ra (backend chat/message-origin.ts) — chỉ nội bộ thấy. */
export type MessageSourceKind =
  | 'contact' | 'system' | 'staff' | 'ai_suggest' | 'ai' | 'ai_external' | 'automation' | 'outside' | 'zns' | 'unknown'
export interface MessageSource {
  kind: MessageSourceKind
  ref: string | null
  /** Tên nhân viên / nhân viên AI / quy tắc / mẫu ZNS. */
  label: string | null
}

/** Nhóm bộ lọc tin trong khung chat (lọc tại máy chủ). */
export type SenderFilter = 'all' | 'contact' | 'ai' | 'staff' | 'other'

export interface ChatMessage {
  id: string
  conversationId?: string
  externalMsgId?: string | null
  senderUid?: string | null
  senderName: string | null
  senderType: SenderType
  content: string | null
  contentType: string
  attachments?: MessageAttachment[]
  sentAt: string
  isDeleted?: boolean
  aiGenerated?: boolean
  reply?: { content: string; msgType: string } | null
  /**
   * Cảm xúc đã thả lên tin này. Backend đã chuẩn hoá mã Zalo (`/-heart`) thành
   * emoji thật trước khi trả về, nên ở đây luôn là emoji hiển thị được.
   */
  reactions?: { emoji: string; reactorId: string; reactorName?: string | null }[]
  /** external = tin với khách; internal = chỉ nhân viên; private = người gửi + được mention. */
  visibility?: 'external' | 'internal' | 'private'
  mentionUserIds?: string[]
  repliedBy?: { fullName: string | null } | null
  /** Tin gửi ra nhưng KHÔNG tới được kênh (mất kết nối, giới hạn, lỗi Zalo…). */
  sendError?: string | null
  /** Ai / cái gì gửi tin này (nội bộ). */
  source?: MessageSource
}

export interface ConversationsResponse {
  conversations: ConversationListItem[]
  total: number
  page: number
  limit: number
}

export interface MessagesResponse {
  messages: ChatMessage[]
  total: number
  page: number
  limit: number
}

export type ResourceKind = 'image' | 'video' | 'file' | 'link'

/** Một tài nguyên (ảnh/video/tệp/liên kết) đã trao đổi trong hội thoại. */
export interface ResourceItem {
  id: string
  messageId: string
  kind: ResourceKind
  url: string
  thumb?: string
  title?: string
  /** Cỡ tệp tính bằng byte — chỉ có với tệp Zalo. */
  size?: number
  sentAt: string
  senderName?: string
}

export interface ConversationResources {
  media: ResourceItem[]
  files: ResourceItem[]
  links: ResourceItem[]
  scanned: number
  truncated: boolean
}

/** Chế độ xem của Hộp thư phía backend (`?view=`) — xem modules/chat/inbox-views.ts. */
export type InboxView = 'mine' | 'unassigned' | 'ai' | 'direct' | 'group' | 'pinned'

export interface ConversationCounts {
  unread: number
  unreplied: number
  total: number
  /** Số hội thoại theo từng chế độ xem (backend cũ không trả → undefined). */
  views?: Record<InboxView, number>
  /** Số hội thoại CHƯA ĐỌC theo từng tài khoản kênh (bỏ lọc tài khoản đang chọn) — chấm báo ở tài khoản khác (lib/inbox-account.ts). Backend cũ không trả → undefined. */
  unreadByAccount?: Record<string, number>
}

// ── Query keys ──────────────────────────────────────────────────────
const keys = {
  list: (params: Record<string, unknown>) => ['conversations', params] as const,
  counts: (params: Record<string, unknown>) => ['conversation-counts', params] as const,
  detail: (id: string) => ['conversation', id] as const,
  messages: (id: string) => ['conversation-messages', id] as const,
  /** Tin đã lọc theo người gửi — con của `messages(id)` để invalidate chung. */
  messagesFiltered: (id: string, sender: string, staffId: string) => ['conversation-messages', id, 'filter', sender, staffId] as const,
  messageSenders: (id: string) => ['conversation-message-senders', id] as const,
  resources: (id: string) => ['conversation-resources', id] as const,
}

// ── Danh sách hội thoại ─────────────────────────────────────────────
export interface ConversationsQueryParams {
  tab?: ConvTab
  search?: string
  unread?: boolean
  unreplied?: boolean
  pinned?: boolean
  accountId?: string
  tag?: string
  /** Kho lưu trữ: chỉ hội thoại của tài khoản đã tạm dừng/đã gỡ. */
  archived?: boolean
  /** Mã nhóm kênh (xem lib/channel-groups.ts) — backend dịch ra danh sách platform. */
  channel?: string
  view?: InboxView
  /** Với view 'ai': chỉ hội thoại của một nhân viên AI (id) hoặc 'default' (AI chung của tổ chức). */
  aiBot?: string
  /** Bộ lọc nâng cao (chuỗi JSON, xem pages/conversations/inbox-conditions.ts). */
  conditions?: string
  logic?: 'and' | 'or'
  /** Chế độ xem tuỳ chỉnh — backend tự lấy điều kiện đã lưu. */
  savedViewId?: string
  page?: number
  limit?: number
}

/** Tham số gửi backend (không gồm `page`/`limit` — mỗi hook tự thêm). */
function conversationsFilterQuery(params: ConversationsQueryParams): Record<string, unknown> {
  const query: Record<string, unknown> = { tab: params.tab }
  if (params.search) query.search = params.search
  if (params.unread) query.unread = 'true'
  if (params.unreplied) query.unreplied = 'true'
  if (params.pinned) query.pinned = 'true'
  if (params.accountId) query.accountId = params.accountId
  if (params.tag) query.tag = params.tag
  if (params.archived) query.archived = '1'
  if (params.channel) query.channel = params.channel
  if (params.view) query.view = params.view
  if (params.view === 'ai' && params.aiBot) query.aiBot = params.aiBot
  if (params.conditions) {
    query.conditions = params.conditions
    query.logic = params.logic ?? 'and'
  } else if (params.savedViewId) query.savedViewId = params.savedViewId
  return query
}

/** Một trang danh sách (hộp thoại chọn hội thoại, tìm kiếm nhanh…). Màn Hộp thư dùng `useInfiniteConversations`. */
export function useConversations(params: ConversationsQueryParams, enabled = true) {
  const query: Record<string, unknown> = {
    ...conversationsFilterQuery(params),
    page: params.page ?? 1,
    limit: params.limit ?? 50,
  }

  return useQuery<ConversationsResponse>({
    queryKey: keys.list(query),
    enabled,
    queryFn: async () => {
      const { data } = await api.get<ConversationsResponse>('/conversations', { params: query })
      return data
    },
    // Lỗi tạm (mạng, 5xx, 429 vượt giới hạn tần suất) → thử lại có giãn cách; hết lượt mà vẫn lỗi thì tự thử tiếp
    // mỗi 20 giây. Trước đây chỉ thử 1 lần rồi kẹt màn "Không tải được…" tới khi có việc khác làm tải lại.
    retry: (count, err) => shouldRetryQuery(count, err),
    retryDelay: (attempt, err) => retryDelayMs(attempt, err),
    refetchInterval: (q) => (q.state.status === 'error' ? ERROR_REFETCH_MS : false),
  })
}

/** Số hội thoại mỗi trang của Hộp thư; cuộn tới cuối danh sách thì tải trang kế (conversation-list.tsx). */
export const CONVERSATIONS_PAGE_SIZE = 50

/**
 * Danh sách Hộp thư nhiều trang. Khoá nằm dưới `['conversations', …]` nên mọi `invalidateQueries(['conversations'])`
 * (realtime, đổi nhãn, ghim…) làm mới TẤT CẢ trang đã mở cùng lúc.
 */
export function useInfiniteConversations(params: ConversationsQueryParams, enabled = true) {
  const query = { ...conversationsFilterQuery(params), limit: CONVERSATIONS_PAGE_SIZE }

  return useInfiniteQuery({
    queryKey: ['conversations', 'infinite', query] as const,
    enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<ConversationsResponse>('/conversations', { params: { ...query, page: pageParam } })
      return data
    },
    getNextPageParam: (last) => nextPageParam(last, last.conversations.length),
    select: (data) => flattenConversationPages(data.pages),
    retry: (count, err) => shouldRetryQuery(count, err),
    retryDelay: (attempt, err) => retryDelayMs(attempt, err),
    refetchInterval: (q) => (q.state.status === 'error' ? ERROR_REFETCH_MS : false),
  })
}

/** Khung gộp làm mới danh sách theo sự kiện realtime. */
export const LIST_REFRESH_WINDOW_MS = 1500
const listRefreshers = new WeakMap<QueryClient, () => void>()

/**
 * Làm mới danh sách hội thoại + bộ đếm khi có sự kiện realtime — GỘP cho cả ứng dụng: lần đầu chạy ngay, các sự kiện
 * trong 1,5 giây sau đó dồn thành một lần. Trước đây mỗi sự kiện, ở MỖI nơi nghe (danh sách + hội thoại đang mở), đều
 * tải lại ngay → tin về dồn dập là cả văn phòng (chung IP) vượt giới hạn tần suất → 429 (lỗi 09/10/2026).
 */
function refreshListsFromRealtime(qc: QueryClient) {
  let run = listRefreshers.get(qc)
  if (!run) {
    run = createCoalescer(() => {
      void qc.invalidateQueries({ queryKey: ['conversations'] })
      void qc.invalidateQueries({ queryKey: ['conversation-counts'] })
    }, LIST_REFRESH_WINDOW_MS)
    listRefreshers.set(qc, run)
  }
  run()
}

export function useConversationCounts(
  tab?: ConvTab,
  archived?: boolean,
  /** Cùng bộ lọc tài khoản/nhóm kênh với danh sách, để số đếm khớp cái đang nhìn. */
  scope?: { accountId?: string; channel?: string },
) {
  const params: Record<string, unknown> = {}
  if (tab) params.tab = tab
  if (archived) params.archived = '1'
  if (scope?.accountId) params.accountId = scope.accountId
  if (scope?.channel) params.channel = scope.channel
  return useQuery<ConversationCounts>({
    queryKey: keys.counts(params),
    queryFn: async () => {
      const { data } = await api.get<ConversationCounts>('/conversations/counts', { params })
      return data
    },
  })
}

// ── Chi tiết 1 hội thoại ────────────────────────────────────────────
export function useConversation(id: string | undefined) {
  return useQuery<ConversationDetail>({
    queryKey: keys.detail(id ?? ''),
    enabled: !!id,
    queryFn: async () => {
      const { data } = await api.get<ConversationDetail>(`/conversations/${id}`)
      return data
    },
  })
}

// ── Danh sách tin nhắn ──────────────────────────────────────────────
/** Số tin mỗi trang. Trang 1 = mới nhất; kéo lên đầu khung chat thì tải trang cũ hơn (xem chat-panel.tsx). */
export const MESSAGES_PAGE_SIZE = 100

/** Cache của `useMessages` là InfiniteData: pages[0] = trang mới nhất (mỗi trang tăng dần theo thời gian). */
type MessagesCache = InfiniteData<MessagesResponse, number>

export function useMessages(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: keys.messages(id ?? ''),
    enabled: !!id,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<MessagesResponse>(`/conversations/${id}/messages`, {
        params: { limit: MESSAGES_PAGE_SIZE, page: pageParam },
      })
      return data
    },
    getNextPageParam: (last) => nextPageParam(last, last.messages.length),
    // Gộp mọi trang đã tải thành một danh sách cũ → mới. Hàm cấp module → tham chiếu ổn định, chỉ tính lại khi dữ liệu đổi.
    select: selectMessages,
  })
}

function selectMessages(data: MessagesCache): MessagesResponse {
  const { messages, total } = flattenMessagePages(data.pages)
  return { messages, total, page: 1, limit: MESSAGES_PAGE_SIZE }
}

/** Sửa tin trong cache `useMessages` (mọi trang đã tải). Chưa có cache thì thôi. */
function updateMessagesCache(qc: QueryClient, convId: string, fn: (pages: MessagesResponse[]) => MessagesResponse[]) {
  qc.setQueryData<MessagesCache>(keys.messages(convId), (old) => (old ? { ...old, pages: fn(old.pages) } : old))
}

/**
 * Tin đã lọc theo người gửi (Khách / AI / Nhân viên / Tự động & khác) — lọc TẠI MÁY CHỦ trên toàn hội thoại,
 * 200 tin gần nhất khớp. `sender = 'all'` → không chạy (dùng useMessages).
 */
export function useFilteredMessages(id: string | undefined, sender: SenderFilter, staffId: string) {
  return useQuery<MessagesResponse>({
    queryKey: keys.messagesFiltered(id ?? '', sender, staffId),
    enabled: !!id && sender !== 'all',
    queryFn: async () => {
      const { data } = await api.get<MessagesResponse>(`/conversations/${id}/messages`, {
        params: { limit: 200, sender, ...(sender === 'staff' && staffId ? { staffId } : {}) },
      })
      return data
    },
  })
}

/** Nhân viên đã gửi tin trong hội thoại (cho bộ lọc "Nhân viên ▸ người"). */
export function useMessageSenders(id: string | undefined, enabled: boolean) {
  return useQuery<{ staff: Array<{ id: string; fullName: string; messages: number }> }>({
    queryKey: keys.messageSenders(id ?? ''),
    enabled: !!id && enabled,
    queryFn: async () => (await api.get(`/conversations/${id}/message-senders`)).data,
  })
}

// ── Tài nguyên đã trao đổi (ảnh/video, tệp, liên kết) ───────────────
export function useConversationResources(id: string | undefined, enabled = true) {
  return useQuery<ConversationResources>({
    queryKey: keys.resources(id ?? ''),
    enabled: !!id && enabled,
    queryFn: async () => {
      const { data } = await api.get<ConversationResources>(`/conversations/${id}/resources`)
      return data
    },
  })
}

// ── Gửi tin nhắn văn bản ────────────────────────────────────────────
/** Tag một người trong nhóm. `pos`/`len` là chỉ số ký tự trong nội dung tin. */
export interface MessageMention {
  pos: number
  uid: string
  len: number
}

export interface SendMessageInput {
  content: string
  replyMessageId?: string
  /** Chỉ có tác dụng với hội thoại nhóm — Zalo bỏ qua ở chat 1-1. */
  mentions?: MessageMention[]
  /** internal/private = tin nội bộ, backend KHÔNG gửi ra kênh khách. */
  visibility?: 'internal' | 'private'
  /** userId nhân viên được @mention trong tin nội bộ. */
  mentionUserIds?: string[]
}

export function useSendMessage(id: string) {
  // Nhận chuỗi (gửi thường) hoặc object khi cần trả lời / tag người.
  return useMutation<ChatMessage, unknown, string | SendMessageInput>({
    mutationFn: async (input) => {
      const body = typeof input === 'string' ? { content: input } : input
      const { data } = await api.post<ChatMessage>(`/conversations/${id}/messages`, body)
      return data
    },
  })
}

// ── Thành viên nhóm (nguồn cho danh sách tag @) ─────────────────────

export interface GroupMember {
  uid: string
  name: string
  avatarUrl: string | null
  isAdmin?: boolean
}

export function useGroupMembers(convId: string | undefined, isGroup: boolean) {
  return useQuery<GroupMember[]>({
    queryKey: ['conversation', convId, 'group-members'],
    enabled: !!convId && isGroup,
    // Thành viên nhóm hiếm khi đổi; giữ lâu để gõ '@' không phải chờ mạng.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await api.get<{ members?: GroupMember[] }>(
        `/conversations/${convId}/group-info`,
      )
      return data.members ?? []
    },
  })
}

// ── Chi tiết bình chọn (card thật trong khung chat) ─────────────────

/** Khớp `PollOptions`/`PollDetail` của zca-js (xem `zalo-thread-tools-routes.ts` GET `/polls/:pollId`). */
export interface PollOption {
  content: string
  votes: number
  voted: boolean
  option_id: number
}
export interface PollDetail {
  question: string
  options: PollOption[]
  closed: boolean
  allow_multi_choices: boolean
  is_anonymous: boolean
  num_vote: number
}

/**
 * Chi tiết bình chọn Zalo (câu hỏi/lựa chọn/số phiếu THẬT) — dùng để vẽ thẻ bình chọn trong khung chat,
 * thay cho dòng thông báo cũ. `pollId` là ID của Zalo (dùng được cho MỌI bình chọn trong nhóm, không chỉ
 * bình chọn tạo từ eCDP) — API đã có sẵn, không cần route mới.
 */
export function usePollDetail(convId: string | undefined, pollId: number | undefined) {
  return useQuery<PollDetail>({
    queryKey: ['conversation', convId, 'poll', pollId],
    enabled: !!convId && !!pollId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await api.get<{ success: boolean; poll: PollDetail }>(
        `/conversations/${convId}/polls/${pollId}`,
      )
      return data.poll
    },
  })
}

/** Đồng bộ lại danh sách thành viên nhóm từ Zalo (bỏ cache). */
export async function refreshGroupMembers(convId: string) {
  const { data } = await api.get<{ members?: GroupMember[] }>(
    `/conversations/${convId}/group-info`,
    { params: { refresh: 1 } },
  )
  return data.members ?? []
}

/** Gỡ một thành viên khỏi nhóm (cần quyền trưởng/phó nhóm trên Zalo). */
export function useRemoveGroupMember(convId: string | undefined) {
  const qc = useQueryClient()
  return useMutation<unknown, unknown, { memberId: string }>({
    mutationFn: async ({ memberId }) => {
      const { data } = await api.post(`/conversations/${convId}/remove-member`, { memberId })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversation', convId, 'group-members'] }),
  })
}

// ── Thao tác trên từng tin nhắn ─────────────────────────────────────

/** Thả / gỡ cảm xúc (gửi chuỗi rỗng để gỡ) */
export function useReactMessage(convId: string) {
  const qc = useQueryClient()
  return useMutation<unknown, unknown, { messageId: string; icon: string }>({
    mutationFn: async ({ messageId, icon }) => {
      const { data } = await api.post(`/conversations/${convId}/messages/${messageId}/reaction`, { icon })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.messages(convId) }),
  })
}

/** Thu hồi tin nhắn trên Zalo (chỉ tin của mình) */
export function useUndoMessage(convId: string) {
  const qc = useQueryClient()
  return useMutation<unknown, unknown, string>({
    mutationFn: async (messageId) => {
      const { data } = await api.post(`/conversations/${convId}/messages/${messageId}/undo`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.messages(convId) }),
  })
}

/** Xoá tin nhắn — mặc định chỉ xoá ở phía mình */
export function useDeleteMessage(convId: string) {
  const qc = useQueryClient()
  return useMutation<unknown, unknown, { messageId: string; onlyMe?: boolean }>({
    mutationFn: async ({ messageId, onlyMe = true }) => {
      const { data } = await api.delete(
        `/conversations/${convId}/messages/${messageId}?onlyMe=${onlyMe}`,
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.messages(convId) }),
  })
}

/** Chuyển tiếp tin nhắn sang nhiều hội thoại */
export function useForwardMessage(convId: string) {
  const qc = useQueryClient()
  return useMutation<
    { sent: number; failed: { conversationId: string; error: string }[] },
    unknown,
    { messageId: string; conversationIds: string[] }
  >({
    mutationFn: async ({ messageId, conversationIds }) => {
      const { data } = await api.post(
        `/conversations/${convId}/messages/${messageId}/forward`,
        { conversationIds },
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  })
}

// ── Đánh dấu đã đọc ─────────────────────────────────────────────────
export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id: string) => {
      await api.post(`/conversations/${id}/mark-read`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation-counts'] })
    },
  })
}

// ── Xoá hội thoại (cần quyền conversations.delete — admin set qua vai trò) ──
export function useDeleteConversation() {
  const qc = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id: string) => {
      await api.delete(`/conversations/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation-counts'] })
    },
  })
}

// ── Đánh dấu là chưa đọc ────────────────────────────────────────────
export function useMarkUnread() {
  const qc = useQueryClient()
  return useMutation<void, unknown, string>({
    mutationFn: async (id: string) => {
      await api.post(`/conversations/${id}/mark-unread`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation-counts'] })
    },
  })
}

// ── Đánh dấu tất cả đã đọc (tuỳ chọn theo tài khoản kênh) ───────────
export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation<{ updated: number }, unknown, string | undefined>({
    mutationFn: async (accountId?: string) => {
      const { data } = await api.post<{ updated: number }>('/conversations/mark-all-read',
        accountId ? { accountId } : {})
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation-counts'] })
    },
  })
}

// ── Ghim / bỏ ghim hội thoại ────────────────────────────────────────
export function useTogglePin() {
  const qc = useQueryClient()
  return useMutation<{ isPinned: boolean }, unknown, { id: string; pin: boolean }>({
    mutationFn: async ({ id, pin }) => {
      const { data } = pin
        ? await api.post<{ isPinned: boolean }>(`/conversations/${id}/pin`)
        : await api.delete<{ isPinned: boolean }>(`/conversations/${id}/pin`)
      return data
    },
    onSuccess: (data, { id }) => {
      qc.setQueryData<ConversationDetail>(keys.detail(id), (prev) =>
        prev ? { ...prev, isPinned: data.isPinned } : prev)
      qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

// ── Chuyển tab Ưu tiên ↔ Nội bộ ─────────────────────────────────────
export function useSetConversationTab() {
  const qc = useQueryClient()
  return useMutation<{ success: boolean; tab: ConvTab }, unknown, { id: string; tab: ConvTab }>({
    mutationFn: async ({ id, tab }) => {
      const { data } = await api.patch<{ success: boolean; tab: ConvTab }>(`/conversations/${id}/tab`, { tab })
      return data
    },
    onSuccess: (data, { id }) => {
      qc.setQueryData<ConversationDetail>(keys.detail(id), (prev) =>
        prev ? { ...prev, tab: data.tab } : prev)
      // Hội thoại rời tab này sang tab kia → cả danh sách lẫn số đếm đều đổi.
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation-counts'] })
    },
  })
}

// ── Thao tác hàng loạt (chọn nhiều hội thoại) ─────────────────────────
export type BulkConvAction = 'mark_read' | 'mark_unread' | 'set_tab' | 'add_tag' | 'remove_tag' | 'delete'

export interface BulkConvInput {
  ids: string[]
  action: BulkConvAction
  tab?: ConvTab
  tag?: string
}

/** POST /conversations/bulk — BE chỉ đụng hội thoại trong phạm vi của người dùng. */
export function useBulkConversations() {
  const qc = useQueryClient()
  return useMutation<{ success: boolean; affected: number; skipped: number }, unknown, BulkConvInput>({
    mutationFn: async (input) => (await api.post('/conversations/bulk', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation-counts'] })
      qc.invalidateQueries({ queryKey: ['conversation'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
    },
  })
}

// ── Tạm dừng AI trong N phút (0 = bỏ tạm dừng) ──────────────────────
export function useSetAiPause(id: string) {
  const qc = useQueryClient()
  return useMutation<{ aiPausedUntil: string | null }, unknown, number>({
    mutationFn: async (minutes: number) => {
      const { data } = await api.patch<{ aiPausedUntil: string | null }>(
        `/conversations/${id}/ai-pause`, { minutes })
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData<ConversationDetail>(keys.detail(id), (prev) =>
        prev ? { ...prev, aiPausedUntil: data.aiPausedUntil } : prev)
      qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

// ── Đổi chế độ AI (manual | auto | suggest) ─────────────────────────
/**
 * Ghi đè luật "chỉ trả lời khi nhắc tên" cho riêng một hội thoại.
 *
 * `null` trả về theo cài đặt chung — người trực nhóm cần bỏ được ghi đè chứ
 * không chỉ bật/tắt, nếu không thì lỡ đặt là mắc kẹt với giá trị đó mãi.
 */
/** Một người bạn / nhóm đã đồng bộ, tìm thấy qua ô tìm kiếm. */
export interface DirectoryHit {
  threadId: string
  threadType: 'user' | 'group'
  name: string
  avatarUrl: string | null
  phone?: string | null
  memberCount?: number
  account: { id: string; displayName: string | null }
  /** Đã có hội thoại thì nhảy thẳng vào, không tạo trùng. */
  conversationId: string | null
}

/**
 * Tìm trong bạn bè + nhóm ĐÃ ĐỒNG BỘ (kể cả những người chưa từng nhắn tin).
 *
 * Danh sách hội thoại chỉ hiện thread đã có tin nhắn, nên nếu không có chỗ này
 * thì muốn nhắn trước cho khách phải ngồi chờ khách nhắn vào.
 */
export function useDirectorySearch(q: string, channelAccountId?: string) {
  const term = q.trim()
  return useQuery<{ friends: DirectoryHit[]; groups: DirectoryHit[] }>({
    queryKey: ['zalo-directory', term, channelAccountId ?? null],
    // Dưới 2 ký tự thì mọi thứ đều khớp — vừa vô nghĩa vừa nặng.
    enabled: term.length >= 2,
    queryFn: async () => {
      const { data } = await api.get('/zalo-directory/search', {
        params: { q: term, ...(channelAccountId ? { channelAccountId } : {}) },
      })
      return data
    },
    placeholderData: (prev) => prev,
  })
}

/** Mở (hoặc tạo) hội thoại với một người bạn / nhóm đã đồng bộ. */
export function useOpenDirectoryThread() {
  const qc = useQueryClient()
  return useMutation<
    { conversationId: string; created: boolean },
    unknown,
    { channelAccountId: string; threadId: string; threadType: 'user' | 'group' }
  >({
    mutationFn: async (body) => {
      const { data } = await api.post('/zalo-directory/open', body)
      return data
    },
    onSuccess: (r) => {
      // Chỉ làm mới danh sách khi vừa TẠO thread — mở cái đã có thì danh sách
      // không đổi, nạp lại chỉ tổ nháy màn hình.
      if (r.created) qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

// ── Bắt đầu cuộc trò chuyện mới (POST /conversations/start) ──────────────────

export type StartConversationMode = 'phone' | 'contact' | 'uid'

export interface StartConversationBody {
  accountId: string
  mode: StartConversationMode
  phone?: string
  contactId?: string
  /** mode 'uid': uid Zalo đã biết sẵn (vd "Nhắn tin riêng" từ một thành viên nhóm) — không tốn lượt tra số. */
  uid?: string
  name?: string
  avatar?: string | null
}

/** Hồ sơ Zalo tra được (bước "Tìm" — dryRun, chưa tạo gì). */
export interface StartPreview {
  profile: { uid: string; name: string; avatar: string | null }
  existingConversationId: string | null
}

export function usePreviewStartConversation() {
  return useMutation<StartPreview, unknown, StartConversationBody>({
    mutationFn: async (body) => {
      const { data } = await api.post<StartPreview>('/conversations/start', { ...body, dryRun: true })
      return data
    },
  })
}

export function useStartConversation() {
  const qc = useQueryClient()
  return useMutation<{ conversationId: string; created: boolean }, unknown, StartConversationBody>({
    mutationFn: async (body) => {
      const { data } = await api.post('/conversations/start', body)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversations'] })
      qc.invalidateQueries({ queryKey: ['conversation-counts'] })
    },
  })
}

export function useSetRequireMention(id: string) {
  const qc = useQueryClient()
  return useMutation<{ requireMention: boolean | null }, unknown, boolean | null>({
    mutationFn: async (requireMention) => {
      const { data } = await api.patch<{ requireMention: boolean | null }>(
        `/conversations/${id}/require-mention`, { requireMention })
      return data
    },
    onSuccess: (data) => {
      qc.setQueryData<ConversationDetail>(keys.detail(id), (prev) =>
        prev ? { ...prev, requireMention: data.requireMention } : prev)
    },
  })
}

// ── Nhận / giao lại hội thoại ────────────────────────────────────────
export interface AssignCandidate {
  id: string
  fullName: string
  avatarUrl: string | null
  role: string
  status: 'online' | 'busy' | 'offline'
  openCount: number
  limit: number
  /** 'all' = quản trị; 'manage' = Quản lý kênh; 'agent' = Tiếp nhận. */
  level: 'all' | 'manage' | 'agent'
}

function refreshAfterAssign(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ queryKey: keys.detail(id) })
  void qc.invalidateQueries({ queryKey: ['conversations'] })
  void qc.invalidateQueries({ queryKey: ['conversation-counts'] })
}

/** Nhận hội thoại chưa ai nhận (hàng chờ chung / kênh mình quản lý). */
export function useClaimConversation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => (await api.post<{ assignedUserId: string }>(`/conversations/${id}/claim`)).data,
    onSettled: () => refreshAfterAssign(qc, id),
  })
}

/** Giao / giao lại hội thoại; `userId: null` = trả về hàng chờ. */
export function useAssignConversation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { userId: string | null; note?: string }) =>
      (await api.post<{ assignedUserId: string | null }>(`/conversations/${id}/assign`, vars)).data,
    onSettled: () => refreshAfterAssign(qc, id),
  })
}

export function useAssignCandidates(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['conversation-assign-candidates', id],
    enabled,
    staleTime: 0,
    queryFn: async () => (await api.get<{ members: AssignCandidate[]; currentAssigneeId: string | null }>(`/conversations/${id}/assign-candidates`)).data,
  })
}

/**
 * Đổi chế độ AI của hội thoại (khung ba tầng): 'follow' = "Theo nhân viên AI" (xoá lựa chọn tay — gửi `aiMode: 'inherit'`),
 * còn lại là lựa chọn tay của nhân viên. Server trả `aiModeReason` thì dùng; không trả thì tự suy để giao diện hiện đúng.
 */
export function useSetAiMode(id: string) {
  const qc = useQueryClient()
  type Res = { aiMode: AiMode; aiModeReason?: string | null; warning?: string | null }
  return useMutation<Res, unknown, ConvAiChoice>({
    mutationFn: async (choice: ConvAiChoice) => {
      const { data } = await api.patch<Res>(`/conversations/${id}/ai-mode`, aiModeRequestBody(choice))
      return data
    },
    onSuccess: (data, choice) => {
      const reason = data.aiModeReason !== undefined ? data.aiModeReason : choice === 'follow' ? null : 'set by staff'
      qc.setQueryData<ConversationDetail>(keys.detail(id), (prev) =>
        prev ? { ...prev, aiMode: data.aiMode ?? prev.aiMode, aiModeReason: reason } : prev,
      )
      void qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}

// ── Cập nhật cache tin nhắn (thêm/dedupe theo id) ───────────────────
function upsertMessage(qc: QueryClient, convId: string, message: ChatMessage) {
  updateMessagesCache(qc, convId, (pages) => {
    if (pages.some((pg) => pg.messages.some((m) => m.id === message.id))) {
      return pages.map((pg) => ({
        ...pg,
        messages: pg.messages.map((m) => (m.id === message.id ? { ...m, ...message } : m)),
      }))
    }
    // Tin mới → cuối trang mới nhất (pages[0]); `total` của MỌI trang tăng 1 để nhất quán khi so với số đã tải.
    return pages.map((pg, i) => ({
      ...pg,
      messages: i === 0 ? [...pg.messages, message] : pg.messages,
      total: pg.total + 1,
    }))
  })
  // Đang xem tin đã lọc → tải lại danh sách lọc (máy chủ quyết định tin mới thuộc nhóm nào).
  void qc.invalidateQueries({ queryKey: [...keys.messages(convId), 'filter'] })
}

function patchMessage(qc: QueryClient, convId: string, messageId: string, patch: Partial<ChatMessage>) {
  updateMessagesCache(qc, convId, (pages) =>
    pages.map((pg) => ({ ...pg, messages: pg.messages.map((m) => (m.id === messageId ? { ...m, ...patch } : m)) })),
  )
  void qc.invalidateQueries({ queryKey: [...keys.messages(convId), 'filter'] })
}

// ── Realtime cho hội thoại đang mở ──────────────────────────────────
export interface ChatRealtimeState {
  typingName: string | null
  aiTyping: boolean
  aiDraft: { suggestionId: string; content: string; confidence: number } | null
  clearAiDraft: () => void
}

/**
 * Join/leave phòng conv, lắng nghe mọi sự kiện chat realtime và cập nhật cache.
 * Tự động rời phòng + gỡ listener khi đổi hội thoại hoặc unmount.
 */
export function useChatRealtime(convId: string | undefined): ChatRealtimeState {
  const qc = useQueryClient()
  const [typingName, setTypingName] = useState<string | null>(null)
  const [aiTyping, setAiTyping] = useState(false)
  const [aiDraft, setAiDraft] = useState<ChatRealtimeState['aiDraft']>(null)
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const aiTypingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAiDraft = useCallback(() => setAiDraft(null), [])

  useEffect(() => {
    if (!convId) return
    const socket = getSocket()
    joinConversation(convId)

    const invalidateList = () => refreshListsFromRealtime(qc)

    const onMessage = (msg: ChatMessage) => {
      const target = msg.conversationId || convId
      upsertMessage(qc, target, msg)
      invalidateList()
    }
    const onConvUpdated = (p?: { convId?: string }) => {
      invalidateList()
      // Hội thoại đang mở đổi dữ liệu (vd auto-bắt SĐT/email vào hồ sơ) →
      // làm mới cả panel chi tiết, không chỉ danh sách.
      if (p?.convId && p.convId === convId) {
        qc.invalidateQueries({ queryKey: ['conversation', convId] })
      }
    }
    const onSeen = () => {
      invalidateList()
      // seenBy nằm trong chi tiết hội thoại → làm mới để avatar "Đã xem" nhảy ngay.
      qc.invalidateQueries({ queryKey: ['conversation', convId] })
    }
    const onAiTyping = (p: { convId: string; isTyping: boolean }) => {
      if (p.convId !== convId) return
      setAiTyping(p.isTyping)
      if (aiTypingTimer.current) clearTimeout(aiTypingTimer.current)
      if (p.isTyping) aiTypingTimer.current = setTimeout(() => setAiTyping(false), 15000)
    }
    const onAiDraft = (p: { convId: string; suggestionId: string; content: string; confidence: number }) => {
      if (p.convId !== convId) return
      setAiDraft({ suggestionId: p.suggestionId, content: p.content, confidence: p.confidence })
    }
    const onAiModeChanged = (p: { convId: string; aiMode: AiMode; aiModeReason?: string | null }) => {
      qc.setQueryData<ConversationDetail>(keys.detail(p.convId), (prev) =>
        prev ? { ...prev, aiMode: p.aiMode, ...(p.aiModeReason !== undefined ? { aiModeReason: p.aiModeReason } : {}) } : prev,
      )
      invalidateList()
    }
    const onEdited = (p: { messageId: string; content: string }) =>
      patchMessage(qc, convId, p.messageId, { content: p.content })
    const onDeleted = (p: { messageId: string }) =>
      patchMessage(qc, convId, p.messageId, { isDeleted: true })
    const onTypingStart = (p: { convId: string; fullName?: string }) => {
      if (p.convId !== convId) return
      setTypingName(p.fullName || i18n.t('convList:hook.customer'))
      if (typingTimer.current) clearTimeout(typingTimer.current)
      typingTimer.current = setTimeout(() => setTypingName(null), 4000)
    }
    const onTypingStop = (p: { convId: string }) => {
      if (p.convId !== convId) return
      setTypingName(null)
    }

    /**
     * Khách thả / gỡ cảm xúc trên Zalo. Backend đã lưu DB và chuẩn hoá emoji,
     * đây chỉ vá cache để hiện ngay mà không phải tải lại hội thoại.
     */
    const onReaction = (p: {
      messageId: string; emoji: string; userId: string; action: 'added' | 'removed'
    }) => {
      updateMessagesCache(qc, convId, (pages) =>
        pages.map((pg) => ({
          ...pg,
          messages: pg.messages.map((m) => {
            if (m.id !== p.messageId) return m
            const rest = (m.reactions ?? []).filter((r) => r.reactorId !== p.userId)
            return {
              ...m,
              reactions: p.action === 'removed' ? rest : [...rest, { emoji: p.emoji, reactorId: p.userId }],
            }
          }),
        })),
      )
    }

    // Ghim/bỏ ghim từ tab hoặc máy khác → làm mới danh sách
    const onPinToggle = () => refreshListsFromRealtime(qc)

    // Reconnect sau khi đứt mạng: có thể đã lỡ tin nhắn trong lúc offline
    // (socket.ts đã tự join lại phòng) → tải lại hội thoại đang mở + danh sách.
    const onConnect = () => {
      qc.invalidateQueries({ queryKey: keys.messages(convId) })
      invalidateList()
    }

    // Tin nội bộ mới: server chỉ ping convId (không kèm nội dung) → refetch
    // qua API vốn đã lọc theo quyền của từng người.
    const onInternalPing = (p: { convId: string }) => {
      if (p.convId === convId) qc.invalidateQueries({ queryKey: keys.messages(convId) })
      invalidateList()
    }

    socket.on('connect', onConnect)
    socket.on('chat:internal-message', onInternalPing)
    socket.on('chat:message', onMessage)
    socket.on('chat:pinned', onPinToggle)
    socket.on('chat:unpinned', onPinToggle)
    socket.on('chat:conv-updated', onConvUpdated)
    socket.on('chat:seen', onSeen)
    socket.on('chat:ai-typing', onAiTyping)
    socket.on('chat:ai-draft', onAiDraft)
    socket.on('chat:ai-mode-changed', onAiModeChanged)
    socket.on('chat:message-edited', onEdited)
    socket.on('chat:message-deleted', onDeleted)
    socket.on('chat:deleted', onDeleted)
    socket.on('chat:reaction-sync', onReaction)
    socket.on('typing:start', onTypingStart)
    socket.on('typing:stop', onTypingStop)

    return () => {
      socket.off('connect', onConnect)
      socket.off('chat:internal-message', onInternalPing)
      socket.off('chat:message', onMessage)
      socket.off('chat:pinned', onPinToggle)
      socket.off('chat:unpinned', onPinToggle)
      socket.off('chat:conv-updated', onConvUpdated)
      socket.off('chat:seen', onSeen)
      socket.off('chat:ai-typing', onAiTyping)
      socket.off('chat:ai-draft', onAiDraft)
      socket.off('chat:ai-mode-changed', onAiModeChanged)
      socket.off('chat:message-edited', onEdited)
      socket.off('chat:message-deleted', onDeleted)
      socket.off('chat:deleted', onDeleted)
      socket.off('chat:reaction-sync', onReaction)
      socket.off('typing:start', onTypingStart)
      socket.off('typing:stop', onTypingStop)
      if (typingTimer.current) clearTimeout(typingTimer.current)
      if (aiTypingTimer.current) clearTimeout(aiTypingTimer.current)
      leaveConversation(convId)
      setTypingName(null)
      setAiTyping(false)
      setAiDraft(null)
    }
  }, [convId, qc])

  return { typingName, aiTyping, aiDraft, clearAiDraft }
}

/**
 * Realtime cấp danh sách hội thoại (phòng org — server tự join khi connect).
 * Mount ở trang Hội thoại để sidebar/badge cập nhật ngay cả khi CHƯA mở
 * hội thoại nào (useChatRealtime chỉ chạy khi có convId).
 */
export function useConversationListRealtime() {
  const qc = useQueryClient()

  useEffect(() => {
    const socket = getSocket()

    const invalidateList = () => refreshListsFromRealtime(qc)

    // Trạng thái tài khoản (zalo:account-status / zalo:connected / connect) do
    // useAccountStatusRealtime nghe TOÀN ỨNG DỤNG (gắn ở AppLayout). Ở đây chỉ còn
    // dùng khi nick bị chuyển sang tổ chức khác.
    const invalidateAccounts = () => {
      qc.invalidateQueries({ queryKey: ['integrations', 'zalo-accounts'] })
    }
    // Tin gửi ra không tới được kênh — báo ngay cả khi người gửi là AI/tự động hoá.
    const onSendError = (e: { convId: string; reason?: string }) => {
      qc.invalidateQueries({ queryKey: keys.messages(e.convId) })
      if (e.reason) toast.error(i18n.t('convList:hook.sendFailed', { reason: e.reason }))
    }

    socket.on('connect', invalidateList) // reconnect → đồng bộ lại danh sách đã lỡ
    // Nick Zalo được đăng nhập ở tổ chức khác → backend tự gỡ khỏi tổ chức này; báo cho người dùng.
    const onZaloRemoved = (e: { message?: string }) => {
      invalidateAccounts()
      invalidateList()
      toast.warning(e.message || i18n.t('convList:hook.zaloRemoved'))
    }
    socket.on('zalo:removed', onZaloRemoved)
    socket.on('chat:send-error', onSendError)
    socket.on('chat:conv-updated', invalidateList)
    socket.on('chat:conv-deleted', invalidateList)
    socket.on('chat:pinned', invalidateList)
    socket.on('chat:unpinned', invalidateList)

    return () => {
      socket.off('connect', invalidateList)
      socket.off('zalo:removed', onZaloRemoved)
      socket.off('chat:send-error', onSendError)
      socket.off('chat:conv-updated', invalidateList)
      socket.off('chat:conv-deleted', invalidateList)
      socket.off('chat:pinned', invalidateList)
      socket.off('chat:unpinned', invalidateList)
    }
  }, [qc])
}
